import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { User, LoginRequest, RegistrationRequest } from '@sulis-liiga/common';
import { STORAGE_KEYS } from '@sulis-liiga/common';
import authService from '../../services/authService';

// Define the state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  // True once the initial session-restore attempt (tryAutoLogin) has finished.
  // Until then, ProtectedRoute must wait rather than redirect to /login, otherwise
  // a full page load of a protected route bounces the user out of a valid session.
  bootstrapped: boolean;
  error: string | null;
  passwordResetRequested: boolean;
  passwordResetSuccess: boolean;
  emailVerificationSuccess: boolean;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  bootstrapped: false,
  error: null,
  passwordResetRequested: false,
  passwordResetSuccess: false,
  emailVerificationSuccess: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(
        credentials.email,
        credentials.password,
        credentials.rememberMe || false,
      );
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to log in. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegistrationRequest, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to register. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logout();
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    return;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to log out. Please try again.';
    return rejectWithValue(errorMessage);
  }
});

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      await authService.deleteAccount();
      return;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete account. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to refresh token. Please log in again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const tryAutoLogin = createAsyncThunk('auth/tryAutoLogin', async (_, { dispatch }) => {
  const storedToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

  if (storedToken && storedUser) {
    try {
      // Try to get a new access token with stored refresh token
      await dispatch(refreshToken());
      return JSON.parse(storedUser);
    } catch (error) {
      // If token refresh fails, clear storage
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      return null;
    }
  }
  return null;
});

export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (email: string, { rejectWithValue }) => {
    try {
      await authService.requestPasswordReset(email);
      return email;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to request password reset. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const confirmPasswordReset = createAsyncThunk(
  'auth/confirmPasswordReset',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      await authService.confirmPasswordReset(token, password);
      return;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token: string, { rejectWithValue }) => {
    try {
      await authService.verifyEmail(token);
      return;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to verify email. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (email: string, { rejectWithValue }) => {
    try {
      await authService.resendVerification(email);
      return email;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to resend verification email. Please try again.';
      return rejectWithValue(errorMessage);
    }
  },
);

// Create the auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    resetPasswordResetState: state => {
      state.passwordResetRequested = false;
      state.passwordResetSuccess = false;
      state.error = null;
    },
    resetEmailVerificationState: state => {
      state.emailVerificationSuccess = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Login cases
    builder.addCase(login.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.error = null;
      // Store user in localStorage
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(action.payload.user));
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = action.payload as string;
    });

    // Register cases
    builder.addCase(register.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state, _action) => {
      state.loading = false;
      state.error = null;
      // Note: we don't set isAuthenticated or user here because we expect
      // the user to log in after registration
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Logout cases
    builder.addCase(logout.pending, state => {
      state.loading = true;
    });
    builder.addCase(logout.fulfilled, state => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      // We still try to clear auth state even if logout fails on server
      state.isAuthenticated = false;
      state.user = null;
    });

    // Delete account (right to be forgotten) — clears the session like logout.
    builder.addCase(deleteAccount.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteAccount.fulfilled, state => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
    });
    builder.addCase(deleteAccount.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Refresh token cases
    builder.addCase(refreshToken.fulfilled, state => {
      state.isAuthenticated = true;
    });
    builder.addCase(refreshToken.rejected, state => {
      state.isAuthenticated = false;
      state.user = null;
    });

    // Auto login cases
    builder.addCase(tryAutoLogin.fulfilled, (state, action) => {
      if (action.payload) {
        state.user = action.payload;
        state.isAuthenticated = true;
      }
      // The initial session-restore attempt is complete (whether or not it
      // found a session); ProtectedRoute may now decide.
      state.bootstrapped = true;
    });
    builder.addCase(tryAutoLogin.rejected, state => {
      state.bootstrapped = true;
    });

    // Request password reset cases
    builder.addCase(requestPasswordReset.pending, state => {
      state.loading = true;
      state.error = null;
      state.passwordResetRequested = false;
    });
    builder.addCase(requestPasswordReset.fulfilled, state => {
      state.loading = false;
      state.passwordResetRequested = true;
      state.error = null;
    });
    builder.addCase(requestPasswordReset.rejected, (state, action) => {
      state.loading = false;
      state.passwordResetRequested = false;
      state.error = action.payload as string;
    });

    // Confirm password reset cases
    builder.addCase(confirmPasswordReset.pending, state => {
      state.loading = true;
      state.error = null;
      state.passwordResetSuccess = false;
    });
    builder.addCase(confirmPasswordReset.fulfilled, state => {
      state.loading = false;
      state.passwordResetSuccess = true;
      state.error = null;
    });
    builder.addCase(confirmPasswordReset.rejected, (state, action) => {
      state.loading = false;
      state.passwordResetSuccess = false;
      state.error = action.payload as string;
    });

    // Verify email cases
    builder.addCase(verifyEmail.pending, state => {
      state.loading = true;
      state.error = null;
      state.emailVerificationSuccess = false;
    });
    builder.addCase(verifyEmail.fulfilled, state => {
      state.loading = false;
      state.emailVerificationSuccess = true;
      state.error = null;
    });
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.loading = false;
      state.emailVerificationSuccess = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, resetPasswordResetState, resetEmailVerificationState } =
  authSlice.actions;
export default authSlice.reducer;
